import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { X, Package, Briefcase, Settings, FlaskRound as Flask, Building2, ChevronRight } from 'lucide-react';
import {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '../ui/command';
import { cn } from '../../lib/utils';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import SkeletonImage from '../common/SkeletonImage';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338';

// Icônes par catégorie
const categoryIcons = {
  products: Package,
  industries: Briefcase,
  services: Settings,
  rds: Flask,
  companies: Building2,
};

const categoryColors = {
  products: 'text-blue-600 bg-blue-50',
  industries: 'text-purple-600 bg-purple-50',
  services: 'text-orange-600 bg-orange-50',
  rds: 'text-[#16A34A] bg-[#16A34A]/10',
  companies: 'text-indigo-600 bg-indigo-50',
};

const SearchModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { search, searchQuery, results, loading, error, clearSearch } = useGlobalSearch();

  // Gérer l'ouverture/fermeture avec la touche Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Empêcher le scroll du body quand le modal est ouvert
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Gérer la navigation au clic sur un résultat
  const handleSelect = useCallback((item) => {
    if (item.link) {
      navigate(item.link);
      onClose();
      clearSearch();
    }
  }, [navigate, onClose, clearSearch]);

  // Fonction pour obtenir l'URL de l'image
  const getImageUrl = (metaImage) => {
    if (!metaImage || !metaImage.url) return null;
    return `${STRAPI_URL}${metaImage.url}`;
  };

  // Fonction pour tronquer le texte
  const truncate = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Highlight du texte matché
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 text-gray-900 font-medium px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Composant pour afficher un item de résultat
  const SearchResultItem = ({ item }) => {
    const Icon = categoryIcons[item.category] || Package;
    const colorClass = categoryColors[item.category] || 'text-gray-600 bg-gray-50';
    const imageUrl = getImageUrl(item.metaImage);

    return (
      <CommandItem
        value={`${item.title} ${item.metaDescription || ''}`}
        onSelect={() => handleSelect(item)}
        className="flex items-start gap-3 p-3 cursor-pointer data-[selected]:bg-[#16A34A]/10 data-[selected]:text-[#0a1628] rounded-lg transition-colors"
      >
        {/* Image thumbnail ou icône */}
        <div className="shrink-0">
          {imageUrl ? (
            <SkeletonImage
              src={imageUrl}
              alt={item.title}
              className="w-12 h-12 rounded-md border border-gray-200"
              imgClassName="w-full h-full object-cover rounded-md"
              icon={false}
            />
          ) : (
            <div className={cn('w-12 h-12 rounded-md flex items-center justify-center', colorClass)}>
              <Icon size={20} />
            </div>
          )}
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {highlightMatch(item.title, searchQuery)}
            </span>
            <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', colorClass)}>
              {item.categoryLabel}
            </span>
          </div>
          {item.metaDescription && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {highlightMatch(truncate(item.metaDescription), searchQuery)}
            </p>
          )}
        </div>

        {/* Flèche de navigation */}
        <ChevronRight size={16} className="text-gray-400 shrink-0 mt-1" />
      </CommandItem>
    );
  };

  // Composant pour afficher un groupe de résultats
  const ResultGroup = ({ title, items, category }) => {
    if (!items || items.length === 0) return null;

    const Icon = categoryIcons[category] || Package;
    const colorClass = categoryColors[category] || 'text-gray-600 bg-gray-50';

    return (
      <CommandGroup heading={
        <div className="flex items-center gap-2 px-2 py-1.5">
          <Icon size={16} className={colorClass.split(' ')[0]} />
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {title}
          </span>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {items.length}
          </span>
        </div>
      }>
        {items.map((item, idx) => (
          <SearchResultItem key={item.id || idx} item={item} />
        ))}
        <CommandSeparator className="my-2" />
      </CommandGroup>
    );
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Command className="rounded-xl border shadow-2xl">
        {/* Header avec input */}
        <div className="flex items-center border-b px-4 py-3">
          <CommandInput
            value={searchQuery}
            onValueChange={search}
            placeholder={intl.formatMessage({ id: 'search.placeholder' })}
            className="flex-1 border-0 bg-transparent text-base focus:ring-0 px-0 placeholder:text-gray-400"
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Clear search"
            >
              <X size={16} className="text-gray-400" />
            </button>
          )}
        </div>

        {/* Liste des résultats */}
        <CommandList className="max-h-[400px] overflow-y-auto p-2">
          {/* État de chargement */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#16A34A]"></div>
              <span className="ml-3 text-gray-500">
                {intl.formatMessage({ id: 'common.loading' })}
              </span>
            </div>
          )}

          {/* État d'erreur */}
          {error && (
            <div className="text-center py-12 text-red-500">
              <p>Error loading search results</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Aucun résultat */}
          {!loading && !error && searchQuery && results.total === 0 && (
            <CommandEmpty className="py-12 text-center">
              <div className="flex flex-col items-center">
                <X size={48} className="text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">
                  {intl.formatMessage({ id: 'search.noResults' }) || 'No results found'}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Try searching for different keywords
                </p>
              </div>
            </CommandEmpty>
          )}

          {/* Résultats groupés par catégorie */}
          {!loading && !error && results.total > 0 && (
            <>
              <ResultGroup
                title={intl.formatMessage({ id: 'search.products' }) || 'Products'}
                items={results.products}
                category="products"
              />
              <ResultGroup
                title={intl.formatMessage({ id: 'search.services' }) || 'Services'}
                items={results.services}
                category="services"
              />
              <ResultGroup
                title={intl.formatMessage({ id: 'search.rd' }) || 'R&D'}
                items={results.rds}
                category="rds"
              />
              <ResultGroup
                title={intl.formatMessage({ id: 'search.industries' }) || 'Industries'}
                items={results.industries}
                category="industries"
              />
              <ResultGroup
                title={intl.formatMessage({ id: 'search.companies' }) || 'Companies'}
                items={results.companies}
                category="companies"
              />

              {/* Footer avec total */}
              <div className="px-4 py-3 text-xs text-gray-400 text-center border-t mt-2">
                {results.total} {results.total === 1 ? 'result' : 'results'} found
              </div>
            </>
          )}

          {/* État initial (pas de recherche) */}
          {!loading && !error && !searchQuery && (
            <div className="py-12 text-center text-gray-500">
              <p className="text-sm">
                {intl.formatMessage({ id: 'search.placeholder' }) || 'Start typing to search...'}
              </p>
            </div>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
};

export default SearchModal;
